import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { EmailTemplatesApi, type EmailTemplateRow } from "../api/emailTemplates";
import SortHeader from "../components/SortHeader";
import { readSort, writeSort, SORT_KEYS, type SortState } from "../lib/tableSort";
import logger from "../analytics/logger";
import JinjaTagHelp from "../components/JinjaTagHelp";
import EmailPreview from "../components/EmailPreview";
import { useEmailTemplateVars } from "../hooks/useEmailTemplateVars";
import { buildVocab } from "../lib/jinjaHighlight";
import { apiErrorMessage } from "../lib/apiErrorMessage";
import { fmtDate } from "../utils/datetime";

type SortField = "name" | "subject" | "description" | "active" | "updated_at";
const SORTABLE: SortField[] = ["name", "subject", "description", "active", "updated_at"];

export default function EmailTemplates() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<EmailTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Client-side column sort. The list is loaded in full (no pagination), so we
  // sort the in-memory rows and persist the choice per-tab like the other lists.
  // The shared default is created_at desc, but this list has no created_at
  // column, so fall back to name asc when the stored sort isn't one of ours.
  const [sortState, setSortState] = useState<SortState>(() => {
    const s = readSort(SORT_KEYS.emailTemplates);
    return SORTABLE.includes(s.sort as SortField) ? s : { sort: "name", dir: "asc" };
  });

  const toggleSort = (field: SortField) => {
    setSortState((prev) => {
      const next: SortState =
        prev.sort === field
          ? { sort: field, dir: prev.dir === "asc" ? "desc" : "asc" }
          : { sort: field, dir: "asc" };
      writeSort(SORT_KEYS.emailTemplates, next);
      return next;
    });
  };

  const sortedRows = useMemo(() => {
    const { sort, dir } = sortState;
    const factor = dir === "asc" ? 1 : -1;
    const field = sort as SortField;
    return [...rows].sort((a, b) => {
      let cmp: number;
      if (field === "active") {
        cmp = Number(a.active) - Number(b.active);
      } else if (field === "updated_at") {
        // ISO-8601 strings compare chronologically as plain strings, so no Date
        // parsing is needed. Compared with < / > rather than localeCompare:
        // locale collation can reorder punctuation (the '-' and ':' separators),
        // which would silently corrupt the ordering. A null (never-updated row)
        // sorts oldest.
        const av = a.updated_at ?? "";
        const bv = b.updated_at ?? "";
        cmp = av < bv ? -1 : av > bv ? 1 : 0;
      } else {
        cmp = (a[field] ?? "")
          .toLowerCase()
          .localeCompare((b[field] ?? "").toLowerCase());
      }
      // Stable tiebreak on name so equal cells keep a predictable order.
      if (cmp === 0) cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      return cmp * factor;
    });
  }, [rows, sortState]);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { vocab: catalog } = useEmailTemplateVars();
  // The Add form's name field decides which variables apply, before the
  // template exists. An unrecognized name resolves to the base set.
  const vocab = useMemo(() => buildVocab(catalog, name || null), [catalog, name]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await EmailTemplatesApi.list());
    } catch (e: unknown) {
      logger.error("email_templates_list_failed", e);
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, []);

  const resetForm = () => {
    setName("");
    setSubject("");
    setDescription("");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    if (!name.trim() || !subject.trim() || !file) {
      setError("Name, subject, and a zipped HTML file are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { uploadUrl, key } = await EmailTemplatesApi.getUploadUrl("application/zip");
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/zip" },
        body: file,
      });
      if (!put.ok) throw new Error(`upload failed: ${put.status}`);
      const created = await EmailTemplatesApi.create({
        name: name.trim(),
        subject: subject.trim(),
        template_location: key,
        description: description.trim() || null,
      });
      resetForm();
      navigate(`/email-templates/${created.id}`);
    } catch (e: unknown) {
      logger.error("email_template_create_failed", e);
      // Prefer `detail`: a rejected upload names the offending variable/link,
      // which is the only actionable part of the message.
      setError(apiErrorMessage(e, "Failed to create template"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: EmailTemplateRow) => {
    if (!window.confirm(`Delete template "${row.name}"?`)) return;
    try {
      await EmailTemplatesApi.remove(row.id);
      await load();
    } catch (e: unknown) {
      logger.error("email_template_delete_failed", e);
      setError("Failed to delete template");
    }
  };

  const shProps = (field: SortField) => ({
    active: sortState.sort === field,
    dir: sortState.dir,
    onToggle: () => toggleSort(field),
  });

  return (
    <Box p={6}>
      <Heading size="lg" mb={4}>
        Email templates
      </Heading>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Stack direction={{ base: "column", md: "row" }} align="flex-start" spacing={4} mb={6}>
      <Box borderWidth="1px" borderRadius="md" p={4} flex="1" maxW="2xl">
        <Heading size="sm" mb={3}>
          Add template
        </Heading>
        <Stack spacing={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Name (unique identifier used to send)
            </Text>
            <Input
              size="sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="vouched-welcome"
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Subject
            </Text>
            <Input
              size="sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Welcome to Human Creator"
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Description (optional)
            </Text>
            <Input
              size="sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Sent to creators when they are vouched"
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Zipped HTML export
            </Text>
            <Text fontSize="xs" color="gray.500" mb={1}>
              From Google Docs: File → Download → Web Page (.html, zipped)
            </Text>
            <Input
              ref={fileRef}
              size="sm"
              type="file"
              accept=".zip,application/zip"
              pt={1}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Box>
          <HStack justify="flex-end">
            <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
              Save
            </Button>
          </HStack>
        </Stack>
      </Box>
      <Box w={{ base: "100%", md: "sm" }}>
        <JinjaTagHelp catalog={catalog} templateName={name || null} />
      </Box>
      </Stack>

      <Box mb={6}>
        <EmailPreview
          html={null}
          emptyText="Preview appears on the template page after saving."
          vocab={vocab}
        />
      </Box>

      {loading ? (
        <Box>
          <Spinner mr={3} />
          <Text as="span">Loading…</Text>
        </Box>
      ) : rows.length === 0 ? (
        <Text color="gray.500">No templates yet.</Text>
      ) : (
        <Table size="sm">
          <Thead>
            <Tr>
              <SortHeader label="Name" {...shProps("name")} />
              <SortHeader label="Subject" {...shProps("subject")} />
              <SortHeader label="Description" {...shProps("description")} />
              <SortHeader label="Active" {...shProps("active")} />
              <SortHeader label="Updated" {...shProps("updated_at")} />
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedRows.map((row) => (
              <Tr
                key={row.id}
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                onClick={() => navigate(`/email-templates/${row.id}`)}
              >
                <Td>{row.name}</Td>
                <Td>{row.subject}</Td>
                <Td color="gray.600">{row.description || "—"}</Td>
                <Td>{row.active ? "Yes" : "No"}</Td>
                <Td color="gray.600" whiteSpace="nowrap">{fmtDate(row.updated_at)}</Td>
                <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    aria-label="Delete template"
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => remove(row)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
